import type { StaticStyles, StyleXStyles } from '@stylexjs/stylex'
import * as stylex from '@stylexjs/stylex'
import type { CSSPropertiesWithExtras } from '@stylexjs/stylex/lib/types/StyleXTypes'
import type { mixin } from '@web/stylex-mixins'

export * from '@stylexjs/stylex'

/**
 * ### ⚠️ CAUTION: use `{...stylex.props(…)}` whenever possible!
 *
 * This compatibility API exists for two specific use cases:
 * 1. Providing style overrides to existing, non-intrinsic components that expect string classnames
 * 2. Mixing styles from different sources
 *
 * 🔴 **Important**: When mixing styles from different sources, StyleX styles always take precedence
 * over other styles due to our CSS layering setup.
 */
export function classNames(
  ...styles: ReadonlyArray<
    StaticStyles<{ [Key in keyof CSSPropertiesWithExtras]: CSSPropertiesWithExtras[Key] | string }>
  >
): string | undefined {
  return stylex.props(styles).className
}

// START KEEP_IN_SYNC_SECTION(stylex_env_mixin)
// `stylex.env.mixin` is build-time data the StyleX babel plugin inlines; the type below
// declares its shape. Every build/test config that wires `@web/stylex-mixins` into StyleX
// via `env: { mixin }` must keep that injection in sync with this declaration:
// KEEP_IN_SYNC(web/bundler/rspack/config/configBuilder.ts)
// KEEP_IN_SYNC(web/storybook/.storybook/main.ts)
// KEEP_IN_SYNC(web/storybook-v2/vite.config.ts)
// KEEP_IN_SYNC(web/packages/ui-platform/src/vitest.config.ts)
// KEEP_IN_SYNC(web/packages/ds-docs-renderer/src/vitest.config.ts)
// KEEP_IN_SYNC(web/packages/ui3-editor/src/vitest.config.ts)
// KEEP_IN_SYNC(web/js/test/stylex_test_setup.ts)
declare module '@stylexjs/stylex/lib/types/StyleXTypes' {
  interface Register {
    env: {
      mixin: typeof mixin
    }
  }
}
// END KEEP_IN_SYNC_SECTION

export default {
  ...stylex,
  classNames,
} as typeof stylex & {
  classNames: typeof classNames
}

export type StyleXStylesWithKeys<Keys extends keyof CSSPropertiesWithExtras> = StyleXStyles<
  Pick<CSSPropertiesWithExtras, Keys>
>

export type StyleXStylesWithoutKeys<Keys extends keyof CSSPropertiesWithExtras> = StyleXStyles<
  Omit<CSSPropertiesWithExtras, Keys>
>
